/**
 * FolderRolesSection — per-account operational-folder preferences, rendered as a
 * section inside the email settings dialog. Lets the user choose which server
 * folder backs each role (Sent / Drafts / Archive / Spam / Trash); "Auto-detect"
 * (empty) falls back to the folder the sync detected from IMAP SPECIAL-USE.
 *
 * Self-contained: resolves its own account (for the saved overrides) + folder
 * list from `accountId`, so it drops into the settings dialog with no extra props.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useEmailFolders, useSetFolderRoleOverrides } from '../hooks/useMailbox';
import { useEmailConnections } from '../hooks/useConnectAccount';

const ROLES = [
  { key: 'sent', label: 'Sent' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'archive', label: 'Archive' },
  { key: 'spam', label: 'Spam' },
  { key: 'trash', label: 'Trash' },
] as const;
type RoleKey = (typeof ROLES)[number]['key'];

const EMPTY: Record<RoleKey, string> = { sent: '', drafts: '', archive: '', spam: '', trash: '' };

export function FolderRolesSection({ accountId }: { accountId: string }) {
  const { data: accounts } = useEmailConnections();
  const account = accounts?.find((a) => a.accountId === accountId);
  const { data: folders } = useEmailFolders(accountId);
  const setOverrides = useSetFolderRoleOverrides();
  const [sel, setSel] = useState<Record<RoleKey, string>>(EMPTY);

  // Seed from the account's saved overrides once the account row is available.
  // Keyed on the account's identity (not its `modules` object) so a save's
  // refetch doesn't clobber an in-progress selection.
  useEffect(() => {
    const current = (account?.modules?.folderRoleOverrides ?? {}) as Partial<
      Record<RoleKey, string>
    >;
    setSel({
      sent: current.sent ?? '',
      drafts: current.drafts ?? '',
      archive: current.archive ?? '',
      spam: current.spam ?? '',
      trash: current.trash ?? '',
    });
  }, [accountId, account?.accountId]);

  const handleSave = () => {
    // Send the full map; '' clears that role's override (null) → auto-detect.
    const overrides: Partial<Record<RoleKey, string | null>> = {};
    for (const { key } of ROLES) overrides[key] = sel[key] ? sel[key] : null;
    setOverrides.mutate({ accountId, overrides });
  };

  return (
    <section className="space-y-2">
      <h3 className="border-b pb-1 text-sm font-semibold">Folders</h3>
      <p className="text-xs text-muted-foreground">
        Choose which server folder each action uses. “Auto-detect” uses the server’s default for
        that role.
      </p>
      <div className="space-y-2">
        {ROLES.map(({ key, label }) => (
          <div key={key} className="grid grid-cols-3 items-center gap-2">
            <Label htmlFor={`role-${key}`}>{label}</Label>
            <select
              id={`role-${key}`}
              aria-label={`${label} folder`}
              className="col-span-2 h-9 rounded-md border bg-background px-2 text-sm"
              value={sel[key]}
              onChange={(e) => setSel((s) => ({ ...s, [key]: e.target.value }))}
            >
              <option value="">Auto-detect</option>
              {(folders ?? []).map((f) => (
                <option key={f.folderId} value={f.folderId}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <Button size="sm" className="gap-1" onClick={handleSave} disabled={setOverrides.isPending}>
        {setOverrides.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          'Save folder preferences'
        )}
      </Button>
    </section>
  );
}

export default FolderRolesSection;
